import React, { useState, useEffect } from 'react';
import { LayoutDashboard, TrendingUp, DollarSign, Users, Activity, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';

const PlatformOverview = () => {
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalStats, setTotalStats] = useState({ revenue: 0, subscribers: 0, content: 0 });

  useEffect(() => {
    fetchPlatformData();
  }, []);

  const fetchPlatformData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Fetch data from all platform hubs
      const platformEndpoints = [
        { name: 'OnlyFans VIP', route: '/crm/onlyfans-vip', api: '/api/onlyfans/stats/vip', color: 'purple' },
        { name: 'OnlyFans Free', route: '/crm/onlyfans-free', api: '/api/onlyfans/stats/free', color: 'green' },
        { name: 'JustForFans', route: '/crm/jff', api: '/api/jff/status', color: 'orange' },
        { name: 'RawFuckClub', route: '/crm/rfc', api: '/api/platform-hubs/rfc/stats', color: 'red' },
        { name: 'Intimaly', route: '/crm/intimaly', api: '/api/platform-hubs/intimaly/stats', color: 'yellow' },
        { name: 'TheBlowers', route: '/crm/theblowers', api: '/api/platform-hubs/theblowers/stats', color: 'cyan' },
        { name: 'Faphouse', route: '/crm/faphouse', api: '/api/faphouse/status', color: 'indigo' }
      ];

      const platformData = await Promise.all(
        platformEndpoints.map(async (platform) => {
          try {
            const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}${platform.api}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
              const data = await res.json();
              return {
                ...platform,
                status: 'active',
                stats: {
                  revenue: data.revenue || data.earnings || 0,
                  subscribers: data.subscribers || data.fans || 0,
                  content: data.posts || data.videos || 0
                }
              };
            }
            return { ...platform, status: 'inactive', stats: { revenue: 0, subscribers: 0, content: 0 } };
          } catch (err) {
            return { ...platform, status: 'error', stats: { revenue: 0, subscribers: 0, content: 0 } };
          }
        })
      );

      setPlatforms(platformData);

      // Calculate totals
      const totals = platformData.reduce((acc, p) => ({
        revenue: acc.revenue + (p.stats?.revenue || 0),
        subscribers: acc.subscribers + (p.stats?.subscribers || 0),
        content: acc.content + (p.stats?.content || 0)
      }), { revenue: 0, subscribers: 0, content: 0 });
      
      setTotalStats(totals);
    } catch (err) {
      console.error('Failed to fetch platform data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'active': { label: 'Active', color: 'bg-green-500/10 text-green-400 border-green-500/20' },
      'inactive': { label: 'Inactive', color: 'bg-zinc-700/50 text-zinc-400 border-zinc-600/20' },
      'error': { label: 'Error', color: 'bg-red-500/10 text-red-400 border-red-500/20' }
    };
    const config = statusConfig[status] || statusConfig.inactive;
    return (
      <span className={`inline-block px-2 py-0.5 text-[10px] font-semibold border rounded ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getPlatformColor = (color) => {
    const colors = {
      'blue': 'from-blue-500/20 to-blue-600/5 border-blue-500/30',
      'purple': 'from-purple-500/20 to-purple-600/5 border-purple-500/30',
      'green': 'from-green-500/20 to-green-600/5 border-green-500/30',
      'pink': 'from-pink-500/20 to-pink-600/5 border-pink-500/30',
      'orange': 'from-orange-500/20 to-orange-600/5 border-orange-500/30',
      'red': 'from-red-500/20 to-red-600/5 border-red-500/30',
      'yellow': 'from-yellow-500/20 to-yellow-600/5 border-yellow-500/30',
      'cyan': 'from-cyan-500/20 to-cyan-600/5 border-cyan-500/30',
      'indigo': 'from-indigo-500/20 to-indigo-600/5 border-indigo-500/30'
    };
    return colors[color] || colors.blue;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <LayoutDashboard size={32} className="text-purple-400" />
            <div>
              <h1 className="text-3xl font-bold">Platform Overview</h1>
              <p className="text-zinc-400 text-sm">Loading platform data...</p>
            </div>
          </div>
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-400"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <LayoutDashboard size={32} className="text-purple-400" />
            <div>
              <h1 className="text-3xl font-bold">Platform Overview</h1>
              <p className="text-zinc-400 text-sm">Unified dashboard across all platforms</p>
            </div>
          </div>
          <button
            onClick={fetchPlatformData}
            className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm font-medium transition"
          >
            <Activity size={16} />
            Refresh All
          </button>
        </div>

        {/* Total Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="p-6 bg-gradient-to-br from-purple-500/20 to-purple-600/5 border border-purple-500/30 rounded-xl">
            <div className="flex items-center gap-2 mb-3">
              <DollarSign size={20} className="text-purple-400" />
              <span className="text-sm text-zinc-400 uppercase tracking-wide">Total Revenue</span>
            </div>
            <p className="text-3xl font-bold">${totalStats.revenue.toLocaleString()}</p>
            <p className="text-xs text-zinc-500 mt-1">Across all platforms</p>
          </div>
          <div className="p-6 bg-gradient-to-br from-blue-500/20 to-blue-600/5 border border-blue-500/30 rounded-xl">
            <div className="flex items-center gap-2 mb-3">
              <Users size={20} className="text-blue-400" />
              <span className="text-sm text-zinc-400 uppercase tracking-wide">Total Subscribers</span>
            </div>
            <p className="text-3xl font-bold">{totalStats.subscribers.toLocaleString()}</p>
            <p className="text-xs text-zinc-500 mt-1">Active subscriptions</p>
          </div>
          <div className="p-6 bg-gradient-to-br from-green-500/20 to-green-600/5 border border-green-500/30 rounded-xl">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp size={20} className="text-green-400" />
              <span className="text-sm text-zinc-400 uppercase tracking-wide">Total Content</span>
            </div>
            <p className="text-3xl font-bold">{totalStats.content.toLocaleString()}</p>
            <p className="text-xs text-zinc-500 mt-1">Posts & videos</p>
          </div>
        </div>

        {/* Platform Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {platforms.map((platform) => (
            <Link
              key={platform.name}
              to={platform.route}
              className="group"
            >
              <div className={`p-6 bg-gradient-to-br ${getPlatformColor(platform.color)} border rounded-xl hover:scale-105 transition-transform duration-200`}>
                {/* Platform Header */}
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-bold mb-1">{platform.name}</h3>
                    {getStatusBadge(platform.status)}
                  </div>
                  <ExternalLink size={16} className="text-zinc-400 group-hover:text-white transition" />
                </div>

                {/* Platform Stats */}
                {platform.status === 'active' && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-zinc-400">Revenue</span>
                      <span className="font-semibold">${(platform.stats?.revenue || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-zinc-400">Subscribers</span>
                      <span className="font-semibold">{(platform.stats?.subscribers || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-zinc-400">Content</span>
                      <span className="font-semibold">{(platform.stats?.content || 0).toLocaleString()}</span>
                    </div>
                  </div>
                )}

                {platform.status === 'inactive' && (
                  <p className="text-sm text-zinc-500">Platform not configured</p>
                )}

                {platform.status === 'error' && (
                  <p className="text-sm text-red-400">Failed to load stats</p>
                )}
              </div>
            </Link>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="mt-8 p-6 bg-zinc-900 border border-zinc-800 rounded-xl">
          <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Link to="/crm/onlyfans-vip" className="p-3 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-sm text-center transition">
              OnlyFans VIP
            </Link>
            <Link to="/crm/jff" className="p-3 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-sm text-center transition">
              JustForFans
            </Link>
            <Link to="/crm/faphouse" className="p-3 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-sm text-center transition">
              Faphouse
            </Link>
            <Link to="/crm/compliance" className="p-3 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-sm text-center transition">
              Compliance
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlatformOverview;
